// Purge físico local (Etapa 15 — spec S05 §Deleção, reescrita nesta etapa).
//
// Escopo desta etapa (só as duas primeiras partes da spec; a terceira —
// purge físico da linha REMOTA no Postgres — é explicitamente um job de
// servidor futuro, fora do MVP; este módulo nunca emite DELETE remoto):
//
//  1. purgeSoftDeleted: apaga fisicamente do SQLite local uma linha de
//     negócio (exceto project_photos, tratada à parte) cujo soft delete já
//     está confirmado sincronizado.
//  2. purgeDeletedPhotos: apaga o arquivo no Storage (e só depois a linha
//     local de project_photos) para fotos confirmadas sincronizadas E fora
//     do período de carência.
//  3. purgeProcessedOutboxItems: apaga fisicamente da própria tabela
//     `outbox` os itens com status='processed' — já confirmados no
//     servidor, sem papel funcional além de auditoria local; diferente das
//     duas acima não depende de hasUnresolvedOutboxItems (é a própria
//     tabela de controle) e NUNCA toca em pending/quarantined.
//
// Restrição dura, comum às duas primeiras: NUNCA purgar uma linha cujo item
// de outbox correspondente ainda está pending/quarantined (ver
// hasUnresolvedOutboxItems em src/db/outbox.ts) — fazer isso perderia a
// exclusão para sempre (o item da outbox passaria a apontar para um
// registro local inexistente, e a linha "ressuscitaria" no próximo pull).
import { eq, isNotNull } from 'drizzle-orm';
import type { SQLiteColumn, SQLiteTable } from 'drizzle-orm/sqlite-core';
import { projects, supplies, projectSupplies, timeEntries, counters, projectPhotos, outbox } from '../db/schema';
import { hasUnresolvedOutboxItems, type AppDatabase } from '../db/outbox';
import { supabase } from '../lib/supabase';
import { PROJECT_PHOTOS_BUCKET } from '../features/projects/photoStoragePath';

// Tabelas de negócio soft-deletáveis SEM arquivo externo associado (mesmo
// conjunto de SoftDeletableTable em src/db/writes.ts, exceto project_photos
// — que precisa apagar o arquivo do Storage antes da linha, ver
// purgeDeletedPhotos). Listadas explicitamente (não via generic) pelo mesmo
// motivo documentado em src/db/writes.ts: Drizzle não infere bem sobre um
// generic que une tabelas heterogêneas.
const PURGABLE_TABLES: Array<{ name: string; table: SQLiteTable }> = [
  { name: 'projects', table: projects },
  { name: 'supplies', table: supplies },
  { name: 'project_supplies', table: projectSupplies },
  { name: 'time_entries', table: timeEntries },
  { name: 'counters', table: counters },
];

function idColumnOf(table: SQLiteTable): SQLiteColumn {
  return (table as unknown as { id: SQLiteColumn }).id;
}

function deletedAtColumnOf(table: SQLiteTable): SQLiteColumn {
  return (table as unknown as { deletedAt: SQLiteColumn }).deletedAt;
}

async function purgeTable(db: AppDatabase, tableName: string, table: SQLiteTable): Promise<void> {
  const rows = (await db.select().from(table).where(isNotNull(deletedAtColumnOf(table)))) as Array<{ id: string }>;

  for (const row of rows) {
    if (await hasUnresolvedOutboxItems(db, tableName, row.id)) continue;
    await db.delete(table).where(eq(idColumnOf(table), row.id));
  }
}

/** Purge local (spec S05 §Deleção) — seguro sem período de carência: não
 * afeta nenhum outro dispositivo, só a cópia local já obsoleta. */
export async function purgeSoftDeleted(db: AppDatabase): Promise<void> {
  for (const { name, table } of PURGABLE_TABLES) {
    await purgeTable(db, name, table);
  }
}

/**
 * ponytail: teto deliberado — um dispositivo que fica offline por mais de
 * PHOTO_PURGE_GRACE_DAYS nunca chega a ver a foto antes dela sumir do
 * Storage (a linha local já era soft-deleted, então a foto não aparece na
 * UI dele de qualquer forma, mas o arquivo em si já não existe se ele
 * tentar baixar por outro caminho). Upgrade: se isso incomodar na prática,
 * subir para um valor maior ou tornar configurável — não há sinal disso
 * ainda.
 */
export const PHOTO_PURGE_GRACE_DAYS = 14;

const PHOTO_PURGE_GRACE_MS = PHOTO_PURGE_GRACE_DAYS * 24 * 60 * 60 * 1000;

/** Subconjunto de `supabase.storage` usado aqui — injetável em teste (mesmo
 * espírito de SupabaseLikeClient em src/sync/index.ts) para não bater em
 * rede de verdade. */
export interface StorageLikeClient {
  from(bucket: string): {
    remove(paths: string[]): PromiseLike<{ error: { message: string } | null }>;
  };
}

/** Purge do arquivo no Storage + linha local de `project_photos` (spec S05
 * §Deleção): só depois de (a) confirmado sincronizado — sem item de outbox
 * pending/quarantined para a foto — E (b) fora do período de carência a
 * partir de `deletedAt`. Usa `row.storagePath` já persistido (nunca
 * reconstrói o path). */
export async function purgeDeletedPhotos(
  db: AppDatabase,
  storageClient: StorageLikeClient = supabase.storage
): Promise<void> {
  const rows = await db.select().from(projectPhotos).where(isNotNull(projectPhotos.deletedAt));
  const now = Date.now();

  for (const row of rows) {
    if (!row.deletedAt) continue;
    if (now - row.deletedAt.getTime() < PHOTO_PURGE_GRACE_MS) continue;
    if (await hasUnresolvedOutboxItems(db, 'project_photos', row.id)) continue;

    const { error } = await storageClient.from(PROJECT_PHOTOS_BUCKET).remove([row.storagePath]);
    if (error) {
      // Falha ao apagar do Storage nunca deve interromper o purge dos
      // outros itens nem propagar para o chamador (SyncManager) — só log.
      console.error(`purgeDeletedPhotos: falha ao apagar arquivo do Storage (${row.storagePath})`, error);
      continue;
    }

    await db.delete(projectPhotos).where(eq(projectPhotos.id, row.id));
  }
}

/** Purge dos itens da própria tabela `outbox` com status='processed' (spec
 * S05 §Deleção) — seguro sem período de carência, mesmo raciocínio de
 * purgeSoftDeleted: um item processed já foi confirmado no servidor, não
 * afeta nenhum outro dispositivo e não tem papel funcional depois disso, só
 * registro de auditoria local que cresceria sem limite se nunca purgado.
 * NUNCA apaga pending/quarantined — esses continuam sendo referenciados
 * (drenagem da fila e tela de debug de quarentena, respectivamente). */
export async function purgeProcessedOutboxItems(db: AppDatabase): Promise<void> {
  await db.delete(outbox).where(eq(outbox.status, 'processed'));
}
